import { ApiError, apiFetch } from './http';
import { buildPortalApiPath } from './routes';

export class UnauthorizedError extends ApiError {
  constructor() {
    super('Unauthorized', 401);
    this.name = 'UnauthorizedError';
  }
}

export interface VerifyMagicLinkResponse {
  authenticated?: boolean;
  pendingLogin?: boolean;
  requiresTerms?: boolean;
  termsVersion?: string;
  expiresAt: number;
  role?: string;
  returnTo?: string;
}

export interface SessionResponse {
  authenticated: boolean;
  expiresAt: number;
  email?: string;
  role?: string;
  groups?: string[];
}

interface VerifyMagicLinkErrorResponse {
  error?: string;
  reason?: string;
}

const SESSION_EXPIRY_KEY = 'microsites-session-expiry';

export function buildSessionScopeKey(clientName: string, programName: string): string {
  return `${clientName}:${programName}`;
}

export function buildSessionExpiryStorageKey(scopeKey: string): string {
  return `${SESSION_EXPIRY_KEY}-${scopeKey}`;
}

export function saveSessionExpiry(scopeKey: string, expiresAt: number): void {
  localStorage.setItem(buildSessionExpiryStorageKey(scopeKey), String(expiresAt));
}

export function clearSessionExpiry(scopeKey: string): void {
  localStorage.removeItem(buildSessionExpiryStorageKey(scopeKey));
}

export function isSessionLocallyValid(scopeKey: string): boolean {
  const raw = localStorage.getItem(buildSessionExpiryStorageKey(scopeKey));
  if (!raw) return false;
  const expiresAt = parseInt(raw, 10);
  if (isNaN(expiresAt)) return false;
  return Math.floor(Date.now() / 1000) < expiresAt;
}

export async function requestMagicLink(email: string, browserSessionId: string, returnTo?: string): Promise<{ message: string }> {
  const body: Record<string, string> = { email, browserSessionId };
  if (returnTo) body.returnTo = returnTo;
  const res = await apiFetch(buildPortalApiPath('/auth/request-link'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new ApiError(`Failed to request sign-in link: ${res.status}`, res.status);
  }

  return res.json() as Promise<{ message: string }>;
}

export async function fetchSession(): Promise<SessionResponse> {
  const res = await apiFetch(buildPortalApiPath('/session'));

  if (res.status === 401) {
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    throw new ApiError(`Failed to check session: ${res.status}`, res.status);
  }

  return res.json() as Promise<SessionResponse>;
}

export interface PendingSessionResponse {
  pendingLogin: boolean;
  email: string;
  role: string;
  requiredTermsVersion: string;
  expiresAt: number;
}

export async function fetchPendingSession(): Promise<PendingSessionResponse> {
  const res = await apiFetch(buildPortalApiPath('/auth/pending-session'));

  if (res.status === 401) {
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    throw new ApiError(`Failed to check pending session: ${res.status}`, res.status);
  }

  return res.json() as Promise<PendingSessionResponse>;
}


export interface SSOExchangeResponse {
  authenticated: boolean;
  expiresAt: number;
  pendingLogin?: boolean;
  requiresTerms?: boolean;
  termsVersion?: string;
}

export async function exchangeSSOTokens(idToken: string, accessToken: string): Promise<SSOExchangeResponse> {
  const res = await apiFetch(buildPortalApiPath('/auth/sso/exchange'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, accessToken }),
  });

  if (res.status === 401 || res.status === 403) {
    throw new ApiError('Your account is not authorized for this application.', res.status);
  }
  if (!res.ok) {
    throw new ApiError(`SSO exchange failed: ${res.status}`, res.status);
  }

  return res.json() as Promise<SSOExchangeResponse>;
}

export async function logoutSession(): Promise<void> {
  const res = await apiFetch(buildPortalApiPath('/auth/logout'), {
    method: 'POST',
  });

  if (res.status === 401) {
    return;
  }
  if (!res.ok) {
    throw new ApiError(`Failed to log out: ${res.status}`, res.status);
  }
}

export async function verifyMagicLink(token: string, browserSessionId: string): Promise<VerifyMagicLinkResponse> {
  const res = await apiFetch(buildPortalApiPath('/auth/verify'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token, browserSessionId }),
  });

  if (!res.ok) {
    const payload = await res.json().catch(() => ({})) as VerifyMagicLinkErrorResponse;
    const message = payload.reason ?? payload.error ?? `Failed to verify sign-in link: ${res.status}`;
    throw new ApiError(message, res.status);
  }

  return res.json() as Promise<VerifyMagicLinkResponse>;
}

export interface AcceptTermsRequest {
  clientName: string;
  email: string;
  version: string;
}

export interface AcceptTermsResponse {
  success: boolean;
  authenticated: boolean;
  expiresAt: number;
}

export async function acceptTerms(request: AcceptTermsRequest): Promise<AcceptTermsResponse> {
  const res = await apiFetch(buildPortalApiPath('/auth/terms/accept'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!res.ok) {
    const payload = await res.json().catch(() => ({})) as { error?: string };
    const message = payload.error ?? `Failed to accept terms: ${res.status}`;
    throw new ApiError(message, res.status);
  }

  return res.json() as Promise<AcceptTermsResponse>;
}